import React from "react";
import logoLight from "../../../../Components/logo.png";
import logoDark from "../../../../Components/logo-dark.png";
import { useThemeMode } from "../../../../theme/ThemeModeContext";
import { useTranslation } from "react-i18next";

// Shared chrome for every /authorize state, so the six cards read as one flow.
// The background gradient matches LoginForm so it is recognisably the same
// product, while the pairing header and heavier card make it clear this is a
// connection screen rather than the normal sign-in.
// The fallback is deliberately generic, never "Claude": defaulting an unnamed
// client to Claude's name is exactly what getClientName exists to prevent.
const OAuthShell = ({ clientName: clientNameProp, children }) => {
  const { t } = useTranslation("auth");
  const clientName = clientNameProp || t("oauth.unknownClient");
  const { resolvedTheme } = useThemeMode();
  const Logo = resolvedTheme === "dark" ? logoDark : logoLight;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light to-ui-page flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-3">
          <img src={Logo} alt="Hajex Bolt" className="h-8 w-auto" />
          <span className="h-px w-8 bg-ui-border-strong" aria-hidden="true" />
          <span className="inline-flex h-8 items-center rounded-md border border-ui-border bg-ui-surface px-3 text-[13px] font-semibold text-ui-text">
            {clientName}
          </span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-ui-border bg-ui-surface shadow-soft-lg">
          {children}
        </div>

        {/* Says where the control ACTUALLY is. Hajex Bolt has no connected-apps
            screen yet, so promising one here would be false at the exact moment
            the user is deciding whether to trust the grant. */}
        <p className="mt-3 text-center text-[11px] leading-relaxed text-ui-text-subtle">
          {t("oauth.neverShares", { client: clientName })}
        </p>
        <p className="mt-2 text-center text-xs text-ui-text-muted">
          {t("login.needHelp")}{" "}
          <a
            href="mailto:info@hajexbolt.com"
            className="font-medium text-brand hover:text-brand-hover"
          >
            {t("login.contactSupport")}
          </a>
        </p>
      </div>
    </div>
  );
};

// Tone classes come from an object lookup, never string interpolation of a
// token fragment, so Tailwind's JIT sees complete class names.
const HEADER_TONES = {
  brand: "bg-brand",
  danger: "bg-state-danger-solid",
};

export const CardHeader = ({ icon, title, subtitle, tone = "brand" }) => (
  <div
    className={`${HEADER_TONES[tone] || HEADER_TONES.brand} px-5 py-4 text-center`}
  >
    {icon && (
      <div className="mx-auto mb-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white">
        {icon}
      </div>
    )}
    <h1 className="text-[17px] font-bold tracking-[-0.2px] text-white">
      {title}
    </h1>
    {subtitle && <p className="mt-1 text-[13px] text-white/80">{subtitle}</p>}
  </div>
);

// The repo's inline button spinner, matching LoginForm's BtnLoader.
export const BtnLoader = () => (
  <div className="flex items-center justify-center">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
  </div>
);

export default OAuthShell;
